import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  CreditCard,
  Clock,
  CheckCircle2,
  Calendar,
  FileText,
  DollarSign,
  Building2,
  Eye,
  Send,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
};

export const VendorPayments = () => {const { t } = useTranslation("procurement");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/procurement/vendor-payments`;
      if (activeTab !== 'all') {
        url += `?status=${activeTab}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch vendor payments');
      const data = await response.json();
      setPayments(data);
    } catch (err) {
      toast.error('Failed to load vendor payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [activeTab]);

  const filteredPayments = payments.filter((payment) => {
    if (!searchTerm) return true;
    return payment.payment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: payments.length,
    pending: payments.filter((p) => p.status === 'pending').length,
    scheduled: payments.filter((p) => p.status === 'scheduled').length,
    completed: payments.filter((p) => p.status === 'completed').length,
    totalAmount: payments.reduce((sum, p) => sum + (p.amount || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="procurement-vendor-payments">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("vendorPayments", "Vendor Payments")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("managePaymentsTo", "Manage payments to suppliers and vendors")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPayments} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-orange-600 hover:bg-orange-700">
            <Plus className="h-4 w-4" />{t("schedulePayment", "Schedule Payment")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-5">
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalPayments", "Total Payments")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("pending", "Pending")}</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Calendar className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("scheduled", "Scheduled")}</p>
                <p className="text-2xl font-bold">{stats.scheduled}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("completed", "Completed")}</p>
                <p className="text-2xl font-bold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalAmount", "Total Amount")}</p>
                <p className="text-2xl font-bold">${(stats.totalAmount / 1000).toFixed(0)}k</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="pending">{t("pending", "Pending")}</TabsTrigger>
                <TabsTrigger value="scheduled">{t("scheduled", "Scheduled")}</TabsTrigger>
                <TabsTrigger value="completed">{t("completed", "Completed")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("payment", "Payment #")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("vendor", "Vendor")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("poReferences", "PO References")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("paymentDate", "Payment Date")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("method", "Method")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) =>
                <tr key={payment.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4">
                      <span className="font-mono text-sm font-medium">{payment.payment_number}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{payment.vendor_name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-wrap gap-1">
                        {payment.po_references?.map((po, idx) =>
                      <Badge key={idx} variant="secondary" className="text-xs">
                            <FileText className="h-3 w-3 mr-1" />
                            {po}
                          </Badge>
                      )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {payment.payment_date ?
                    <div className="flex items-center gap-1.5 text-sm">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          {payment.payment_date}
                        </div> :

                    <span className="text-muted-foreground text-sm">{t("notScheduled", "Not scheduled")}</span>
                    }
                    </td>
                    <td className="py-4 px-4 text-right font-mono font-medium">
                      ${payment.amount?.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-sm">
                      {payment.payment_method || '-'}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Badge className={statusColors[payment.status] || statusColors.pending}>
                        {payment.status === 'completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                        {payment.status === 'scheduled' && <Calendar className="h-3 w-3 mr-1" />}
                        {payment.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                        {payment.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost">
                          <Eye className="h-4 w-4" />
                        </Button>
                        {payment.status === 'pending' &&
                      <Button size="sm" variant="outline" className="gap-1">
                            <Send className="h-3.5 w-3.5" />{t("schedule", "Schedule")}

                      </Button>
                      }
                        {payment.status === 'scheduled' &&
                      <Button size="sm" variant="outline" className="gap-1">
                            <CreditCard className="h-3.5 w-3.5" />{t("process", "Process")}

                      </Button>
                      }
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredPayments.length === 0 &&
          <div className="py-12 text-center">
              <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noPaymentsFound", "No payments found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};